import { useRef, useEffect, useReducer } from "react";
import { personalInfo } from "../data/index";

const initialState = {
  name: "",
  email: "",
  subject: "",
  message: "",
  status: "idle",
};

function reducer(state, action) {
  switch (action.type) {
    case "UPDATE_FIELD":
      return { ...state, [action.field]: action.value };
    case "SUBMITTING":
      return { ...state, status: "submitting" };
    case "SUCCESS":
      return { ...initialState, status: "success" };
    case "ERROR":
      return { ...state, status: "error" };
    case "RESET":
      return { ...state, status: "idle" };
    default:
      return state;
  }
}

export default function Contact() {
  const headerRef = useRef(null);
  const [form, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = "1";
            entry.target.style.transform = "translateY(0)";
          }
        });
      },
      { threshold: 0.1 },
    );
    if (headerRef.current) observer.observe(headerRef.current);
    return () => observer.disconnect();
  }, []);

  function handleChange(e) {
    dispatch({
      type: "UPDATE_FIELD",
      field: e.target.name,
      value: e.target.value,
    });
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    dispatch({ type: "SUBMITTING" });

    setTimeout(() => {
      dispatch({ type: "SUCCESS" });
      setTimeout(() => dispatch({ type: "RESET" }), 4000);
    }, 1500);
  }

  const inputStyle = {
    width: "100%",
    background: "#1a1a1a",
    border: "1px solid #2e2e2e",
    borderRadius: "10px",
    padding: "14px 18px",
    color: "#f5f5f5",
    fontSize: "0.95rem",
    fontFamily: "Inter, sans-serif",
    outline: "none",
    transition: "border-color 0.3s ease",
    boxSizing: "border-box",
  };

  const labelStyle = {
    display: "block",
    color: "#a0a0a0",
    fontSize: "0.85rem",
    fontWeight: 600,
    marginBottom: "8px",
    letterSpacing: "0.5px",
  };

  const contactLinks = [
    {
      icon: "📧",
      label: "Email",
      value: personalInfo.email,
      href: `mailto:${personalInfo.email}`,
    },
    {
      icon: "📱",
      label: "Phone",
      value: personalInfo.phone,
      href: `tel:${personalInfo.phone}`,
    },
    {
      icon: "🐙",
      label: "GitHub",
      value: "github.com/TheNarh",
      href: personalInfo.github,
    },
    {
      icon: "💼",
      label: "LinkedIn",
      value: "https://www.linkedin.com/in/ludwig-sackey-narh-670078106/",
      href: personalInfo.linkedin,
    },
  ];

  return (
    <section
      id="contact"
      style={{
        padding: "120px 24px",
        maxWidth: "1100px",
        margin: "0 auto",
      }}
    >
      <div
        ref={headerRef}
        style={{
          opacity: 0,
          transform: "translateY(30px)",
          transition: "all 0.7s ease",
        }}
      >
        <p
          style={{
            color: "#22d3ee",
            fontSize: "0.85rem",
            fontWeight: 600,
            letterSpacing: "3px",
            textTransform: "uppercase",
            marginBottom: "12px",
          }}
        >
          Contact
        </p>

        <h2
          style={{
            fontSize: "clamp(2rem, 4vw, 2.8rem)",
            fontWeight: 800,
            color: "#f5f5f5",
            marginBottom: "16px",
            letterSpacing: "-1px",
          }}
        >
          Let's build something together
        </h2>

        <p
          style={{
            color: "#a0a0a0",
            fontSize: "1.05rem",
            maxWidth: "560px",
            lineHeight: 1.8,
            marginBottom: "60px",
          }}
        >
          I am actively seeking global opportunities. Whether you have a role, a
          project, or just want to talk engineering, my inbox is always open.
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "48px",
            alignItems: "start",
          }}
        >
          {/* LEFT — contact links */}
          <div
            style={{ display: "flex", flexDirection: "column", gap: "16px" }}
          >
            {contactLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  background: "#242424",
                  border: "1px solid #2e2e2e",
                  borderRadius: "14px",
                  padding: "20px 24px",
                  textDecoration: "none",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#22d3ee";
                  e.currentTarget.style.transform = "translateX(6px)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 30pxrgba(34, 211, 238),0.08)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#2e2e2e";
                  e.currentTarget.style.transform = "translateX(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div
                  style={{
                    width: "44px",
                    height: "44px",
                    background: "rgba(57,255,20,0.08)",
                    border: "1px solidrgba(34, 211, 238),0.15)",
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1.2rem",
                    flexShrink: 0,
                  }}
                >
                  {link.icon}
                </div>
                <div>
                  <p
                    style={{
                      color: "#666",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                      marginBottom: "2px",
                    }}
                  >
                    {link.label}
                  </p>
                  <p
                    style={{
                      color: "#f5f5f5",
                      fontSize: "0.9rem",
                      fontWeight: 500,
                    }}
                  >
                    {link.value}
                  </p>
                </div>
                <span
                  style={{
                    marginLeft: "auto",
                    color: "#22d3ee",
                    fontSize: "1rem",
                  }}
                >
                  →
                </span>
              </a>
            ))}

            <div
              style={{
                background: "rgba(57,255,20,0.05)",
                border: "1px solidrgba(34, 211, 238),0.15)",
                borderRadius: "14px",
                padding: "24px",
                marginTop: "8px",
              }}
            >
              <p
                style={{
                  color: "#22d3ee",
                  fontWeight: 700,
                  fontSize: "1rem",
                  marginBottom: "8px",
                }}
              >
                🟢 Available for opportunities
              </p>
              <p
                style={{
                  color: "#a0a0a0",
                  fontSize: "0.875rem",
                  lineHeight: 1.7,
                }}
              >
                Open to full-time roles, contract work, and global remote
                positions. Response time: within 24 hours.
              </p>
            </div>
          </div>

          {/* RIGHT — contact form */}
          <div
            style={{
              background: "#242424",
              border: "1px solid #2e2e2e",
              borderRadius: "20px",
              padding: "40px",
            }}
          >
            <h3
              style={{
                color: "#f5f5f5",
                fontWeight: 700,
                fontSize: "1.1rem",
                marginBottom: "28px",
              }}
            >
              Send a message
            </h3>

            <form
              onSubmit={handleSubmit}
              style={{ display: "flex", flexDirection: "column", gap: "20px" }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "16px",
                }}
              >
                <div>
                  <label style={labelStyle}>Your Name</label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="John Smith"
                    style={inputStyle}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#22d3ee";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#2e2e2e";
                    }}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="john@company.com"
                    style={inputStyle}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#22d3ee";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#2e2e2e";
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={labelStyle}>Subject</label>
                <input
                  type="text"
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  placeholder="Job opportunity / Project / Just saying hi"
                  style={inputStyle}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#22d3ee";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#2e2e2e";
                  }}
                />
              </div>

              <div>
                <label style={labelStyle}>Message</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Tell me about your project or opportunity..."
                  rows={5}
                  style={{
                    ...inputStyle,
                    resize: "vertical",
                    minHeight: "120px",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#22d3ee";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#2e2e2e";
                  }}
                />
              </div>

              <button
                type="submit"
                disabled={form.status === "submitting"}
                style={{
                  background: form.status === "success" ? "#00cc44" : "#22d3ee",
                  color: "#1a1a1a",
                  padding: "16px 32px",
                  borderRadius: "10px",
                  fontSize: "1rem",
                  fontWeight: 700,
                  border: "none",
                  cursor:
                    form.status === "submitting" ? "not-allowed" : "pointer",
                  transition: "all 0.3s ease",
                  fontFamily: "Inter, sans-serif",
                  opacity: form.status === "submitting" ? 0.7 : 1,
                }}
                onMouseEnter={(e) => {
                  if (form.status !== "submitting") {
                    e.currentTarget.style.boxShadow =
                      "0 0 30pxrgba(34, 211, 238),0.4)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = "none";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                {form.status === "submitting"
                  ? "Sending..."
                  : form.status === "success"
                    ? "✓ Message Sent!"
                    : "Send Message →"}
              </button>

              {form.status === "success" && (
                <p
                  style={{
                    color: "#22d3ee",
                    fontSize: "0.875rem",
                    textAlign: "center",
                  }}
                >
                  Thanks! I will get back to you within 24 hours.
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
